import {
    patchCenterUpdateMember,
    PatchCenterUpdateMemberParamsType,
    patchChangeCoachForMember,
    patchChangeCoachForMembers,
    PatchChangeCoachForMembersParamsType,
    patchCoachPermission,
    PatchCoachPermissionRequestType,
    patchDisconnectMember,
    PatchDisconnectMemberParamsType,
    patchDisconnectMembers,
    PatchDisconnectMembersParamsType,
    patchMemberNotificationSettingsAPI,
    patchSessionReviewPermission,
    PatchSessionReviewPermissionRequestType,
    postAssignCoach,
    PostAssignCoachParamsType,
} from '../api/center';
import { MemberProfile } from './useMemberQueryService';
import { updateTrainerPermissionAPI, UpdateTrainerPermissionParamsType } from '../api/employee';
import { getItemWithExpiration } from '../lib/utils';
import { invalidateScheduleListQueries } from '../lib/scheduleQueryKeys';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MemberNotificationSettingsPatchBody } from '../lib/memberNotificationSettings';

type Props = {
    fetchCoachList?: boolean;
    onPatchCenterUpdateMemberSuccess?: (data: MemberProfile) => void;
    onPatchCenterUpdateMemberError?: (error: any) => void;
    onPatchDisconnectMembersSuccess?: () => void;
    /** 실패 응답 `{ status, data }` 를 그대로 받는다. data.errorCode 로 원인을 구분한다 */
    onPatchDisconnectMembersError?: (error: any) => void;
    onPatchDisconnectMemberSuccess?: () => void;
    /** 실패 응답 `{ status, data }` 를 그대로 받는다. data.errorCode 로 원인을 구분한다 */
    onPatchDisconnectMemberError?: (error: any) => void;
    onPatchChangeCoachForMemberSuccess?: (data: any) => void;
    onPatchChangeCoachForMemberError?: (error: any) => void;
    onPatchChangeCoachForMembersSuccess?: (data: any) => void;
    onPatchChangeCoachForMembersError?: (error: any) => void;
    onPatchUpdateTrainerPermissionSuccess?: () => void;
    onPatchUpdateTrainerPermissionError?: (error: any) => void;
    onPostAssignCoachSuccess?: (data: any) => void;
    onPostAssignCoachError?: (error: any) => void;
};

const useCenterMutationService = ({
    onPatchCenterUpdateMemberSuccess,
    onPatchCenterUpdateMemberError,
    onPatchDisconnectMemberSuccess,
    onPatchDisconnectMemberError,
    onPatchDisconnectMembersSuccess,
    onPatchDisconnectMembersError,
    onPatchChangeCoachForMemberSuccess,
    onPatchChangeCoachForMemberError,
    onPatchChangeCoachForMembersSuccess,
    onPatchChangeCoachForMembersError,
    onPatchUpdateTrainerPermissionSuccess,
    onPatchUpdateTrainerPermissionError,
    onPostAssignCoachSuccess,
    onPostAssignCoachError,
}: Props) => {
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();

    const invalidateMemberDomainQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberList'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
            queryClient.invalidateQueries({ queryKey: ['getTrainerMemberCountAPI'] }),
            queryClient.invalidateQueries({ queryKey: ['getUnpaidPaymentsQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getSalesListQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] }),
            queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterSalesSettlementCalendarQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleFeeQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterRemainingScheduleSessionQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['groupTicket'] }),
            queryClient.invalidateQueries({ queryKey: ['publicMember'] }),
            queryClient.invalidateQueries({ queryKey: ['memberCoach'] }),
        ]);
    };

    const patchCenterUpdateMemberMutation = useMutation({
        mutationFn: async (params: PatchCenterUpdateMemberParamsType) => await patchCenterUpdateMember(params),
        onSuccess: async (res) => {
            if (res.status === 200) {
                onPatchCenterUpdateMemberSuccess && onPatchCenterUpdateMemberSuccess(res.data.member);
                void invalidateMemberDomainQueries();
                /**
                 * 회원 표시명이 일정 문서에 스냅샷으로 박혀 있어서, 이름을 고치면 일정 목록도
                 * 다시 받아야 한다. 회원 도메인 무효화(`invalidateMemberDomainQueries`)는
                 * 담당 강사 변경·연결 해제와 공유하는 자리라 거기에 넣지 않고 **회원 정보
                 * 수정에만** 붙인다 — 강사 변경·연결 해제는 일정 목록의 표시를 바꾸지 않는다.
                 */
                invalidateScheduleListQueries(queryClient);
            } else if (res.status === 400 || res.status === 401 || res.status === 403 || res.status === 404) {
                onPatchCenterUpdateMemberError && onPatchCenterUpdateMemberError(res.status);
            }
        },
    });
    const patchDisconnectMembersMutation = useMutation({
        mutationFn: async (params: PatchDisconnectMembersParamsType) => await patchDisconnectMembers(params),
        onSuccess: async (res) => {
            if (res?.status === 200) {
                onPatchDisconnectMembersSuccess && onPatchDisconnectMembersSuccess();
                void invalidateMemberDomainQueries();
                return;
            }
            // 공유 풀 소유자 차단(409)처럼 상태코드를 나열해 두지 않은 실패도 호출부가 알아야 한다.
            onPatchDisconnectMembersError && onPatchDisconnectMembersError(res);
        },
    });
    const patchDisconnectMemberMutation = useMutation({
        mutationFn: async (params: PatchDisconnectMemberParamsType) => await patchDisconnectMember(params),
        onSuccess: async (res) => {
            if (res?.status === 200) {
                onPatchDisconnectMemberSuccess && onPatchDisconnectMemberSuccess();
                void invalidateMemberDomainQueries();
                return;
            }
            onPatchDisconnectMemberError && onPatchDisconnectMemberError(res);
        },
    });

    // 담당 강사 변경
    const patchChangeCoachForMemberMutation = useMutation({
        mutationFn: async (params: PatchDisconnectMemberParamsType & { newCoachId: string }) =>
            await patchChangeCoachForMember({ ...params, accessToken: authorization }),
        onSuccess: async (res) => {
            if (res?.status === 200) {
                onPatchChangeCoachForMemberSuccess && onPatchChangeCoachForMemberSuccess(res.data);
                void invalidateMemberDomainQueries();
                return;
            }
            // 공유 소유자·환불 진행 중(409)처럼 나열해 두지 않은 실패도 호출부가 사유를 알려야 한다.
            onPatchChangeCoachForMemberError &&
                onPatchChangeCoachForMemberError({ status: res?.status ?? 0, data: res?.data });
        },
    });

    // 담당 강사 변경
    const patchChangeCoachForMembersMutation = useMutation({
        mutationFn: async (params: PatchChangeCoachForMembersParamsType) =>
            await patchChangeCoachForMembers({ ...params, accessToken: authorization }),
        onSuccess: async (res) => {
            if (res?.status === 200) {
                onPatchChangeCoachForMembersSuccess && onPatchChangeCoachForMembersSuccess(res.data);
                void invalidateMemberDomainQueries();
                return;
            }
            // 공유 소유자·환불 진행 중(409)처럼 나열해 두지 않은 실패도 호출부가 사유를 알려야 한다.
            onPatchChangeCoachForMembersError &&
                onPatchChangeCoachForMembersError({ status: res?.status ?? 0, data: res?.data });
        },
    });

    // 수강권 권한 변경 : (PATCH)
    const patchUpdateTrainerPermission = useMutation({
        mutationFn: async (params: UpdateTrainerPermissionParamsType) =>
            await updateTrainerPermissionAPI({ ...params, accessToken: authorization }),
        onSuccess: (res) => {
            if (res.status === 200) {
                onPatchUpdateTrainerPermissionSuccess && onPatchUpdateTrainerPermissionSuccess();
            } else {
                onPatchUpdateTrainerPermissionError && onPatchUpdateTrainerPermissionError(res.status);
            }
        },
    });

    // 강사별 세션리뷰 권한 일괄 수정 : (PATCH)
    const patchSessionReviewPermissionMutation = useMutation({
        mutationFn: async (params: PatchSessionReviewPermissionRequestType) =>
            await patchSessionReviewPermission({ ...params, accessToken: authorization }),
    });
    // 강사별 권한 일괄 수정 : (PATCH)
    const patchCoachPermissionMutation = useMutation({
        mutationFn: async (params: PatchCoachPermissionRequestType) =>
            await patchCoachPermission({ ...params, accessToken: authorization }),
    });

    /**
     * 회원 알림 설정 변경 : (PATCH)
     *
     * 성공·실패 모두 `{ status, data }` 로 resolve 한다 — 성공 판정은 `status === 200` 만.
     * 캐시 갱신은 여기서 하지 않는다. 이번 모달 열림의 쿼리 키를 아는 `useMemberNotificationSettings` 가 한다.
     * networkMode 'always': 오프라인이면 React Query 가 요청을 멈춰 두는데, 그러면 저장 중 상태가 풀리지 않는다.
     *                       바로 보내서 네트워크 실패(`status: 0`)로 돌려받는다.
     */
    const patchMemberNotificationSettingsMutation = useMutation({
        mutationFn: async (params: { centerId: string; body: MemberNotificationSettingsPatchBody }) =>
            await patchMemberNotificationSettingsAPI({
                ...params,
                // 토큰은 요청 시점에 읽는다
                accessToken: getItemWithExpiration('authorization'),
            }),
        networkMode: 'always',
    });

    // 담당 강사 최초 배정
    const postAssignCoachMutation = useMutation({
        mutationFn: async (params: Omit<PostAssignCoachParamsType, 'accessToken'>) =>
            await postAssignCoach({ ...params, accessToken: authorization }),
        onSuccess: async (res) => {
            if (res?.status === 200) {
                onPostAssignCoachSuccess && onPostAssignCoachSuccess(res.data);
                void invalidateMemberDomainQueries();
            } else {
                onPostAssignCoachError && onPostAssignCoachError(res?.status);
            }
        },
    });

    return {
        patchCenterUpdateMemberMutation,
        patchDisconnectMembersMutation,
        patchDisconnectMemberMutation,
        patchChangeCoachForMemberMutation,
        patchChangeCoachForMembersMutation,
        patchUpdateTrainerPermission,
        patchSessionReviewPermissionMutation,
        patchCoachPermissionMutation,
        patchMemberNotificationSettingsMutation,
        postAssignCoachMutation,
    };
};

export default useCenterMutationService;
